import React from 'react'
import { normalizeResolvedVideoCallQuality } from '../../routing/videoCallQualityContract'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function pickDefined(source, keys) {
  return keys.reduce((result, key) => {
    if (source[key] !== undefined) {
      result[key] = source[key]
    }

    return result
  }, {})
}

function normalizeBoolean(value, fallbackValue) {
  return typeof value === 'boolean' ? value : fallbackValue
}

function mergePlainObject(baseValue, nextValue) {
  if (!isPlainObject(nextValue)) {
    return baseValue
  }

  return {
    ...baseValue,
    ...nextValue,
  }
}

export const HUNDRED_MS_RTC_LAYOUTS = Object.freeze({
  grid: 'grid',
  pin: 'pin',
})

export const HUNDRED_MS_RTC_ADAPTER_CAPABILITIES = Object.freeze({
  screenshare: true,
  remoteMute: false,
  auditObserverJoin: false,
  playbackDeviceSelection: true,
  deviceHotSwap: true,
  customVideoPlaceholder: true,
  usernameOverlay: true,
  rtmBackedPresence: false,
})

export function createHundredMsRtcAdapterCapabilities(overrides = {}) {
  return {
    ...HUNDRED_MS_RTC_ADAPTER_CAPABILITIES,
    ...(isPlainObject(overrides) ? overrides : {}),
  }
}

export function normalizeHundredMsRtcLayout(
  layout,
  fallbackValue = HUNDRED_MS_RTC_LAYOUTS.pin,
) {
  if (layout === 0 || layout === '0') {
    return HUNDRED_MS_RTC_LAYOUTS.grid
  }

  if (layout === 1 || layout === '1') {
    return HUNDRED_MS_RTC_LAYOUTS.pin
  }

  if (typeof layout !== 'string') {
    return fallbackValue
  }

  const normalizedLayout = layout.trim().toLowerCase()
  if (normalizedLayout === 'grid') {
    return HUNDRED_MS_RTC_LAYOUTS.grid
  }

  if (normalizedLayout === 'pin' || normalizedLayout === 'pinned') {
    return HUNDRED_MS_RTC_LAYOUTS.pin
  }

  return fallbackValue
}

export function createHundredMsRtcCompatibilityProps({
  rtcProps = {},
  styleProps = {},
  callbacks = {},
} = {}) {
  const safeRtcProps = isPlainObject(rtcProps) ? rtcProps : {}

  return {
    rtcProps: {
      ...pickDefined(safeRtcProps, [
        'authToken',
        'userName',
        'role',
        'roomId',
        'roomName',
        'userId',
        'peerMetadata',
        'cameraDeviceId',
        'microphoneDeviceId',
        'playbackDeviceId',
        'CustomVideoPlaceholder',
      ]),
      rememberDeviceSelection: normalizeBoolean(safeRtcProps.rememberDeviceSelection, true),
      autoManageVideo: normalizeBoolean(safeRtcProps.autoManageVideo, true),
      autoManageWakeLock: normalizeBoolean(safeRtcProps.autoManageWakeLock, true),
      enableAudio: normalizeBoolean(safeRtcProps.enableAudio, true),
      enableVideo: normalizeBoolean(safeRtcProps.enableVideo, true),
      enableScreensharing: normalizeBoolean(safeRtcProps.enableScreensharing, false),
      layout: normalizeHundredMsRtcLayout(safeRtcProps.layout),
      videoQuality: normalizeResolvedVideoCallQuality(safeRtcProps.videoQuality),
    },
    styleProps: isPlainObject(styleProps) ? styleProps : {},
    callbacks: isPlainObject(callbacks) ? callbacks : {},
  }
}

export function mergeHundredMsRtcCompatibilityProps(baseProps = {}, nextProps = {}) {
  return createHundredMsRtcCompatibilityProps({
    ...baseProps,
    ...nextProps,
    rtcProps: mergePlainObject(baseProps?.rtcProps || {}, nextProps?.rtcProps),
    styleProps: mergePlainObject(baseProps?.styleProps || {}, nextProps?.styleProps),
    callbacks: mergePlainObject(baseProps?.callbacks || {}, nextProps?.callbacks),
  })
}

let hundredMsRtcModulePromise = null

export function loadHundredMsRtcModule() {
  if (!hundredMsRtcModulePromise) {
    hundredMsRtcModulePromise = import('./HundredMsRtcCompatibilityShell').then((module) => ({
      HundredMsRtcCompatibilityShell:
        module.HundredMsRtcCompatibilityShell || module.default,
    }))
  }

  return hundredMsRtcModulePromise
}

export function preloadHundredMsRtcModule() {
  return loadHundredMsRtcModule().then(() => undefined)
}

const LazyHundredMsRtcCompatibilityShell = React.lazy(async () => {
  const module = await loadHundredMsRtcModule()

  return {
    default: module.HundredMsRtcCompatibilityShell,
  }
})

export function HundredMsRtcCompatibilitySurface({ fallback = null, ...props }) {
  return React.createElement(
    React.Suspense,
    { fallback },
    React.createElement(LazyHundredMsRtcCompatibilityShell, {
      fallback,
      ...props,
    }),
  )
}

HundredMsRtcCompatibilitySurface.displayName = 'HundredMsRtcCompatibilitySurface'
